import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { 
  AlertTriangle, 
  Loader2, 
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Trash2,
  Zap,
  AlertCircle,
  Users,
  Check
} from 'lucide-react';
import { ticketService } from '../../services/api';
import { cn } from '../../lib/utils';

// Helper function to normalize category
const normalizeCategory = (cat) => {
  if (!cat) return '';
  return String(cat).toUpperCase().replace(/\s+/g, '');
};

export const DoublonsModal = ({ open, onOpenChange, currentProject, onRefreshData }) => {
  const [loading, setLoading] = useState(false);
  const [doublons, setDoublons] = useState([]);
  const [ventesDoublons, setVentesDoublons] = useState([]); // NEW: duplicate VENTE lines
  const [totalDoublons, setTotalDoublons] = useState(0);
  const [totalSurplus, setTotalSurplus] = useState(0);
  const [totalVentesDoublons, setTotalVentesDoublons] = useState(0); // NEW
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedToKeep, setSelectedToKeep] = useState(new Set());
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState(null);
  const [autoCleaningInProgress, setAutoCleaningInProgress] = useState(false);
  const [autoCleaningStats, setAutoCleaningStats] = useState(null);
  const [activeTab, setActiveTab] = useState('attributions'); // 'attributions', 'ventes', or 'imports'
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  
  // Import duplicates state
  const [importDuplicates, setImportDuplicates] = useState([]);
  const [totalImportDuplicates, setTotalImportDuplicates] = useState(0);
  const [selectedImportDeletes, setSelectedImportDeletes] = useState(new Set());
  const [deletingImports, setDeletingImports] = useState(false);
  
  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState(null);

  // Function to clean duplicate VENTES
  const handleCleanDuplicates = async () => {
    setCleaningDuplicates(true);
    try {
      const result = await ticketService.cleanDuplicates(currentProject);
      setResult({
        success: true,
        message: `✅ ${result.duplicates_deleted} doublons supprimés`
      });
      loadDoublons(); // Refresh
      onRefreshData?.(); // Refresh main data
    } catch (error) {
      setResult({
        success: false,
        message: `❌ Erreur: ${error.message}`
      });
    } finally {
      setCleaningDuplicates(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadDoublons();
    }
  }, [open, currentProject]);

  const loadDoublons = async () => {
    setLoading(true);
    setResult(null);
    setAutoCleaningStats(null);
    try {
      const [data, importData] = await Promise.all([
        ticketService.getDoublons(currentProject),
        ticketService.getImportDuplicates(currentProject)
      ]);
      setDoublons(data.doublons || []);
      setTotalDoublons(data.total_doublons || 0);
      setTotalSurplus(data.total_surplus || 0);
      // Load duplicate VENTES
      setVentesDoublons(data.ventes_doublons || []);
      setTotalVentesDoublons(data.total_ventes_doublons || 0);
      // Load import duplicates
      setImportDuplicates(importData.groups || []);
      setTotalImportDuplicates(importData.total_groups || 0);
      setCurrentIndex(0);
      setSelectedToKeep(new Set());
      setSelectedImportDeletes(new Set());
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const currentDoublon = doublons[currentIndex];

  // Group ACHAT tickets by email account
  const groupedAchats = useMemo(() => {
    if (!currentDoublon?.achats) return [];
    
    const groups = {};
    currentDoublon.achats.forEach(achat => {
      const key = achat.email_compte || achat.mail_client || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          email: key,
          tickets: [],
          totalQty: 0
        };
      }
      groups[key].tickets.push(achat);
      groups[key].totalQty += achat.qty || 1;
    });
    
    return Object.values(groups);
  }, [currentDoublon]);

  // Check if selection is valid (exact match to required qty)
  const requiredQty = currentDoublon?.vente_qty || 0;
  const selectedQty = useMemo(() => {
    if (!currentDoublon?.achats) return 0;
    return currentDoublon.achats
      .filter(a => selectedToKeep.has(a.id))
      .reduce((sum, a) => sum + (a.qty || 1), 0);
  }, [selectedToKeep, currentDoublon]);
  
  const isSelectionValid = selectedQty === requiredQty;

  // Toggle keeping a SINGLE ticket - INDIVIDUAL selection
  const toggleKeep = (ticketId) => {
    if (!currentDoublon?.achats) return;
    
    const newSet = new Set(selectedToKeep);
    
    if (newSet.has(ticketId)) {
      // Deselect this ticket
      newSet.delete(ticketId);
    } else {
      // Select this ticket (no limit check - user can select freely)
      newSet.add(ticketId);
    }
    
    setSelectedToKeep(newSet);
  };

  // Select/deselect entire account group (header click only)
  const toggleAccountGroup = (email) => {
    if (!currentDoublon?.achats) return;
    
    const accountTickets = currentDoublon.achats.filter(
      a => (a.email_compte || a.mail_client) === email
    ) || [];
    
    if (accountTickets.length === 0) return;
    
    const allSelected = accountTickets.every(t => selectedToKeep.has(t.id));
    const newSet = new Set(selectedToKeep);
    
    accountTickets.forEach(t => {
      if (allSelected) {
        newSet.delete(t.id);
      } else {
        newSet.add(t.id);
      }
    });
    
    setSelectedToKeep(newSet);
  };

  // Check for issues before fixing
  const checkForIssues = () => {
    if (!currentDoublon) return null;
    
    const selectedTickets = currentDoublon.achats?.filter(a => selectedToKeep.has(a.id)) || [];
    const venteTicket = currentDoublon.vente;
    const issues = [];
    
    // Check category mismatch
    const venteCategory = normalizeCategory(venteTicket?.categorie);
    const selectedCategories = [...new Set(selectedTickets.map(t => normalizeCategory(t.categorie)))];
    
    if (venteCategory && selectedCategories.length > 0) {
      const mismatch = selectedCategories.some(cat => cat && cat !== venteCategory);
      if (mismatch) {
        issues.push({
          type: 'category',
          message: `Catégorie différente détectée: VENTE=${venteTicket.categorie}, ACHAT=${selectedCategories.join(', ')}`
        });
      }
    }
    
    // Check non-adjacent seats (if rang and place are filled)
    if (selectedTickets.length > 1) {
      const ticketsWithPlaces = selectedTickets.filter(t => t.rang && t.place);
      if (ticketsWithPlaces.length > 1) {
        // Group by rang
        const byRang = {};
        ticketsWithPlaces.forEach(t => {
          if (!byRang[t.rang]) byRang[t.rang] = [];
          byRang[t.rang].push(parseInt(t.place) || 0);
        });
        
        // Check if places are consecutive within each rang
        let nonAdjacent = false;
        Object.entries(byRang).forEach(([rang, places]) => {
          if (places.length > 1) {
            places.sort((a, b) => a - b);
            for (let i = 1; i < places.length; i++) {
              if (places[i] - places[i-1] !== 1) {
                nonAdjacent = true;
                break;
              }
            }
          }
        });
        
        // Check if multiple rangs
        if (Object.keys(byRang).length > 1) {
          nonAdjacent = true;
        }
        
        if (nonAdjacent) {
          issues.push({
            type: 'adjacent',
            message: 'Places non adjacentes détectées (rangs ou places différents)'
          });
        }
      }
    }
    
    return issues.length > 0 ? issues : null;
  };

  // Find better suggestion
  const findSuggestion = () => {
    if (!currentDoublon || !currentDoublon.achats) return null;
    
    const venteTicket = currentDoublon.vente;
    const venteCategory = normalizeCategory(venteTicket?.categorie);
    const requiredQty = currentDoublon.vente_qty;
    
    // Find account groups that match exactly
    const matchingGroups = groupedAchats.filter(group => {
      // Check if total qty matches
      if (group.totalQty !== requiredQty) return false;
      
      // Check if category matches
      const categoryMatches = group.tickets.every(t => {
        const cat = normalizeCategory(t.categorie);
        return !venteCategory || !cat || cat === venteCategory;
      });
      
      return categoryMatches;
    });
    
    if (matchingGroups.length > 0) {
      // Return the first matching group that's not already selected
      const currentSelectedEmails = new Set(
        currentDoublon.achats
          .filter(a => selectedToKeep.has(a.id))
          .map(a => a.email_compte || a.mail_client)
      );
      
      const alternative = matchingGroups.find(g => !currentSelectedEmails.has(g.email));
      if (alternative) {
        return alternative;
      }
    }
    
    return null;
  };

  const handleFixCurrent = async (forceValidation = false) => {
    if (!currentDoublon || !isSelectionValid) return;
    
    // Check for issues (only if not forcing)
    if (!forceValidation) {
      const issues = checkForIssues();
      if (issues && issues.length > 0) {
        const suggestion = findSuggestion();
        setAlertDialog({
          issues: issues,
          suggestion: suggestion,
          refVente: currentDoublon.ref_vente
        });
        return;
      }
    }
    
    setFixing(true);
    try {
      const ticketIds = Array.from(selectedToKeep);
      const response = await fetch(
        `/api/tickets/fix-doublon?ref_vente=${currentDoublon.ref_vente}&project=${currentProject}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticket_ids_to_keep: ticketIds, validate: true })
        }
      );
      const data = await response.json();
      
      setResult({ ok: true, msg: data.message });
      
      if (currentIndex < doublons.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setSelectedToKeep(new Set());
      } else {
        loadDoublons();
      }
      
      if (onRefreshData) onRefreshData();
    } catch (e) {
      setResult({ ok: false, msg: 'Erreur lors de la correction' });
    }
    setFixing(false);
  };
  
  // Alert dialog handlers (defined outside to avoid closure issues)
  const handleAlertForce = () => {
    setAlertDialog(null);
    handleFixCurrent(true);
  };
  
  const handleAlertCancel = () => {
    setAlertDialog(null);
  };
  
  const handleUseSuggestion = () => {
    if (alertDialog && alertDialog.suggestion) {
      const newSet = new Set();
      alertDialog.suggestion.tickets.forEach(t => newSet.add(t.id));
      setSelectedToKeep(newSet);
    }
    setAlertDialog(null);
  };

  const handleDesattribuerAll = async () => {
    if (!currentDoublon) return;
    
    setFixing(true);
    try {
      const response = await fetch(
        `/api/tickets/fix-doublon?ref_vente=${currentDoublon.ref_vente}&project=${currentProject}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ticket_ids_to_keep: [] })
        }
      );
      
      setResult({ ok: true, msg: `Tous désattribués pour ref ${currentDoublon.ref_vente}` });
      
      if (currentIndex < doublons.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setSelectedToKeep(new Set());
      } else {
        loadDoublons();
      }
      
      if (onRefreshData) onRefreshData();
    } catch (e) {
      setResult({ ok: false, msg: 'Erreur' });
    }
    setFixing(false);
  };

  // AUTO CLEANING - Process all doublons automatically
  const handleAutoCleaning = async () => {
    if (doublons.length === 0) return;
    
    setAutoCleaningInProgress(true);
    setAutoCleaningStats({ processed: 0, fixed: 0, skipped: 0, errors: 0 });
    
    let fixed = 0;
    let skipped = 0;
    let errors = 0;
    
    for (let i = 0; i < doublons.length; i++) {
      const doublon = doublons[i];
      
      try {
        // Find the best automatic match - SIMPLIFIED: take first N tickets
        const bestMatch = findBestAutoMatch(doublon);
        
        if (bestMatch && bestMatch.ticketIds.length > 0) {
          const response = await fetch(
            `/api/tickets/fix-doublon?ref_vente=${doublon.ref_vente}&project=${currentProject}`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ticket_ids_to_keep: bestMatch.ticketIds, validate: true })
            }
          );
          
          if (response.ok) {
            fixed++;
          } else {
            errors++;
          }
        } else {
          skipped++;
        }
      } catch (e) {
        errors++;
      }
      
      setAutoCleaningStats({ processed: i + 1, fixed, skipped, errors });
    }
    
    setAutoCleaningInProgress(false);
    setResult({ ok: true, msg: `Auto-nettoyage terminé: ${fixed} corrigés, ${skipped} ignorés, ${errors} erreurs` });
    
    loadDoublons();
    if (onRefreshData) onRefreshData();
  };

  // Find best automatic match for a doublon - SIMPLIFIED LOGIC
  // Just takes the first N tickets where N = required quantity
  const findBestAutoMatch = (doublon) => {
    if (!doublon || !doublon.achats || doublon.achats.length === 0) return null;
    
    const requiredQty = doublon.vente_qty || 0;
    if (requiredQty <= 0) return null;
    
    // Simply take the first N tickets to match the required quantity
    const ticketIds = [];
    let totalQty = 0;
    
    for (const achat of doublon.achats) {
      const qty = achat.qty || 1;
      if (totalQty + qty <= requiredQty) {
        ticketIds.push(achat.id);
        totalQty += qty;
      }
      if (totalQty >= requiredQty) break;
    }
    
    // Only return if we found exactly the required quantity
    if (totalQty === requiredQty && ticketIds.length > 0) {
      return { ticketIds, totalQty };
    }
    
    return null;
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setSelectedToKeep(new Set());
    }
  };

  const handleNext = () => {
    if (currentIndex < doublons.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedToKeep(new Set());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-[#121212] border-[#C4A052]/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Détection des Doublons
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Stats + Auto Clean */}
          <div className="flex gap-4 p-3 bg-zinc-800/50 rounded-lg items-center">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-500">{totalDoublons}</p>
              <p className="text-xs text-zinc-400">Attributions en trop</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-500">{totalVentesDoublons}</p>
              <p className="text-xs text-zinc-400">VENTES dupliquées</p>
            </div>
            <div className="flex-1 flex gap-2 justify-end">
              <Button 
                onClick={handleAutoCleaning} 
                disabled={loading || autoCleaningInProgress || doublons.length === 0}
                className="bg-emerald-600 hover:bg-emerald-500"
                size="sm"
              >
                {autoCleaningInProgress ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                AUTO NETTOYAGE
              </Button>
              <Button onClick={loadDoublons} variant="outline" disabled={loading} size="sm">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Actualiser'}
              </Button>
            </div>
          </div>

          {/* Tabs for different types */}
          {(totalDoublons > 0 || totalVentesDoublons > 0 || totalImportDuplicates > 0) && (
            <div className="flex gap-2 border-b border-zinc-700 pb-2">
              <button
                onClick={() => { setActiveTab('attributions'); setCurrentIndex(0); }}
                className={cn(
                  "px-4 py-2 rounded-t text-sm font-medium transition-colors",
                  activeTab === 'attributions' 
                    ? "bg-amber-500/20 text-amber-400 border-b-2 border-amber-500" 
                    : "text-zinc-400 hover:text-white"
                )}
              >
                Attributions en trop ({totalDoublons})
              </button>
              <button
                onClick={() => { setActiveTab('ventes'); setCurrentIndex(0); }}
                className={cn(
                  "px-4 py-2 rounded-t text-sm font-medium transition-colors",
                  activeTab === 'ventes' 
                    ? "bg-red-500/20 text-red-400 border-b-2 border-red-500" 
                    : "text-zinc-400 hover:text-white"
                )}
              >
                VENTES dupliquées ({totalVentesDoublons})
              </button>
              <button
                onClick={() => { setActiveTab('imports'); setCurrentIndex(0); }}
                className={cn(
                  "px-4 py-2 rounded-t text-sm font-medium transition-colors",
                  activeTab === 'imports' 
                    ? "bg-sky-500/20 text-sky-400 border-b-2 border-sky-500" 
                    : "text-zinc-400 hover:text-white"
                )}
                data-testid="imports-tab"
              >
                Imports dupliqués ({totalImportDuplicates})
              </button>
            </div>
          )}

          {/* Auto cleaning progress */}
          {autoCleaningInProgress && autoCleaningStats && (
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-emerald-400 font-medium mb-2">
                Auto-nettoyage en cours... {autoCleaningStats.processed}/{doublons.length}
              </p>
              <div className="w-full bg-zinc-700 rounded-full h-2">
                <div 
                  className="bg-emerald-500 h-2 rounded-full transition-all"
                  style={{ width: `${(autoCleaningStats.processed / doublons.length) * 100}%` }}
                />
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                ✓ {autoCleaningStats.fixed} corrigés | ⏭ {autoCleaningStats.skipped} ignorés | ✗ {autoCleaningStats.errors} erreurs
              </p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#C4A052]" />
            </div>
          ) : (totalDoublons === 0 && totalVentesDoublons === 0 && totalImportDuplicates === 0) ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
              <p className="text-xl text-white">Aucun doublon détecté !</p>
              <p className="text-zinc-400">Toutes les attributions sont correctes.</p>
            </div>
          ) : activeTab === 'ventes' && ventesDoublons.length > 0 ? (
            /* ============ VENTES DUPLIQUÉES TAB ============ */
            <>
              <div className="border border-red-500/30 rounded-lg p-4 bg-red-500/5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-6 w-6 text-red-500" />
                    <span className="text-red-400 font-bold text-lg">
                      {totalVentesDoublons} références VENTE avec des lignes en double
                    </span>
                  </div>
                  <Button
                    onClick={handleCleanDuplicates}
                    disabled={cleaningDuplicates}
                    className="bg-red-600 hover:bg-red-500 text-white"
                  >
                    {cleaningDuplicates ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    SUPPRIMER TOUS LES DOUBLONS
                  </Button>
                </div>
                
                <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                  {ventesDoublons.map((vd, idx) => (
                    <div key={vd.ref_vente || idx} className="border border-red-500/20 rounded-lg p-3 bg-zinc-800/50">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="text-red-400 font-bold text-lg">Réf: {vd.ref_vente}</span>
                          <span className="ml-3 px-2 py-1 bg-red-500/20 text-red-400 rounded text-sm">
                            {vd.vente_count} lignes identiques
                          </span>
                        </div>
                      </div>
                      
                      {/* Show all duplicate VENTE lines */}
                      <div className="space-y-2">
                        <p className="text-zinc-400 text-sm font-medium">Lignes VENTE en double :</p>
                        {vd.ventes?.map((v, vIdx) => (
                          <div key={v.id || vIdx} className="p-2 bg-purple-500/10 border border-purple-500/30 rounded text-sm">
                            <p className="text-white font-medium">{v.evenement}</p>
                            <p className="text-zinc-300">
                              {v.categorie} | Qty: {v.qty} | {v.prix}€ | 
                              Client: {v.nom_client || '-'} | Date: {v.date_event}
                            </p>
                            <p className="text-zinc-500 text-xs mt-1">ID: {v.id}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : activeTab === 'imports' && importDuplicates.length > 0 ? (
            /* ============ IMPORT DUPLICATES TAB ============ */
            <>
              <div className="border border-sky-500/30 rounded-lg p-4 bg-sky-500/5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-6 w-6 text-sky-500" />
                    <span className="text-sky-400 font-bold text-lg">
                      {importDuplicates.reduce((s, g) => s + g.surplus, 0)} ticket(s) en double détecté(s) dans {importDuplicates.length} groupe(s)
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10"
                      onClick={() => {
                        // Select all suggested deletes
                        const allSuggested = new Set();
                        importDuplicates.forEach(g => g.suggested_delete?.forEach(id => allSuggested.add(id)));
                        setSelectedImportDeletes(allSuggested);
                      }}
                      data-testid="select-all-suggested"
                    >
                      Tout sélectionner ({importDuplicates.reduce((s, g) => s + g.surplus, 0)})
                    </Button>
                    <Button
                      onClick={async () => {
                        if (selectedImportDeletes.size === 0) return;
                        setDeletingImports(true);
                        try {
                          const res = await ticketService.deleteImportDuplicates(
                            Array.from(selectedImportDeletes), currentProject
                          );
                          setResult({ ok: true, msg: `${res.deleted_count} doublon(s) supprimé(s)` });
                          loadDoublons();
                          onRefreshData?.();
                        } catch (e) {
                          setResult({ ok: false, msg: `Erreur: ${e.message}` });
                        }
                        setDeletingImports(false);
                      }}
                      disabled={selectedImportDeletes.size === 0 || deletingImports}
                      className="bg-red-600 hover:bg-red-500 text-white"
                      data-testid="delete-import-duplicates"
                    >
                      {deletingImports ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      SUPPRIMER ({selectedImportDeletes.size})
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                  {importDuplicates.map((group, gIdx) => (
                    <div key={group.key || gIdx} className="border border-zinc-700 rounded-lg p-3 bg-zinc-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className={cn(
                            "px-2 py-0.5 rounded text-xs font-bold",
                            group.type === 'ACHAT' ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                          )}>
                            {group.type}
                          </span>
                          <span className="text-white font-bold">Réf: {group.ref}</span>
                          <span className="text-zinc-400 text-sm">M{group.match_num}</span>
                          <span className="text-zinc-400 text-sm">{group.categorie}</span>
                          {group.rang && <span className="text-zinc-500 text-sm">Rang: {group.rang}</span>}
                          {group.place && <span className="text-zinc-500 text-sm">Place: {group.place}</span>}
                        </div>
                        <span className="px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-sm font-bold">
                          {group.count}x ({group.surplus} en trop)
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        {group.tickets?.map((t, tIdx) => {
                          const isFirst = tIdx === 0;
                          const isSelected = selectedImportDeletes.has(t.id);
                          return (
                            <div
                              key={t.id || tIdx}
                              onClick={() => {
                                if (isFirst) return; // Don't allow selecting the original
                                const newSet = new Set(selectedImportDeletes);
                                if (isSelected) newSet.delete(t.id);
                                else newSet.add(t.id);
                                setSelectedImportDeletes(newSet);
                              }}
                              className={cn(
                                "flex items-center justify-between p-2 rounded text-sm transition-colors",
                                isFirst 
                                  ? "bg-emerald-500/10 border border-emerald-500/20" 
                                  : isSelected
                                    ? "bg-red-500/15 border border-red-500/30 cursor-pointer"
                                    : "bg-zinc-700/30 border border-zinc-700 cursor-pointer hover:bg-zinc-700/50"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                {isFirst ? (
                                  <Check className="h-4 w-4 text-emerald-400" />
                                ) : (
                                  <div className={cn(
                                    "w-4 h-4 rounded border-2 flex items-center justify-center",
                                    isSelected ? "border-red-500 bg-red-500" : "border-zinc-500"
                                  )}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                )}
                                <span className={isFirst ? "text-emerald-400 font-medium" : isSelected ? "text-red-300" : "text-zinc-300"}>
                                  {isFirst ? "GARDER (original)" : "DOUBLON"}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-zinc-400">
                                <span>{t.evenement?.substring(0, 30)}</span>
                                <span>Rang: {t.rang || '-'}</span>
                                <span>Place: {t.place || '-'}</span>
                                <span>{t.prix ? `${t.prix}€` : ''}</span>
                                <span className="text-zinc-600">{t.source || ''}</span>
                                <span className="text-zinc-600 text-[10px]">{t.id?.substring(0, 8)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : activeTab === 'attributions' && currentDoublon ? (
            <>
              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button onClick={handlePrevious} disabled={currentIndex === 0} variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Précédent
                </Button>
                <span className="text-zinc-400">
                  Doublon {currentIndex + 1} / {doublons.length}
                </span>
                <Button onClick={handleNext} disabled={currentIndex >= doublons.length - 1} variant="outline" size="sm">
                  Suivant <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {/* Current doublon details */}
              <div className="border border-amber-500/30 rounded-lg p-4 bg-amber-500/5">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <span className="text-amber-400 font-bold">
                    Réf Vente: {currentDoublon.ref_vente}
                  </span>
                  <span className="text-zinc-400">|</span>
                  <span className="text-white">
                    Quantité VENTE: <span className="text-emerald-400 font-bold">{currentDoublon.vente_qty}</span>
                  </span>
                  <span className="text-zinc-400">|</span>
                  <span className="text-white">
                    Attribué à: <span className="text-red-400 font-bold">{currentDoublon.achat_count}</span> ACHAT
                  </span>
                  <span className="text-red-400 text-sm">
                    (+{currentDoublon.surplus} en trop)
                  </span>
                </div>

                {/* VENTE info */}
                {currentDoublon.vente && (
                  <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded mb-3">
                    <p className="text-purple-400 font-semibold mb-1">VENTE</p>
                    <p className="text-white font-medium text-lg">{currentDoublon.vente.evenement}</p>
                    <p className="text-white text-base mt-1">
                      {currentDoublon.vente.categorie} | Qty: {currentDoublon.vente.qty} | 
                      Client: {currentDoublon.vente.nom_client} ({currentDoublon.vente.mail_client})
                    </p>
                  </div>
                )}

                {/* ACHAT list - grouped by account */}
                <div className="space-y-3">
                  <p className="text-white font-semibold text-lg">
                    Sélectionnez les {currentDoublon.vente_qty} ACHAT à garder:
                  </p>
                  
                  {groupedAchats.map((group, gIdx) => {
                    const allSelected = group.tickets.every(t => selectedToKeep.has(t.id));
                    const someSelected = group.tickets.some(t => selectedToKeep.has(t.id));
                    
                    return (
                      <div 
                        key={group.email || gIdx}
                        className={cn(
                          "rounded-lg border transition-colors overflow-hidden",
                          allSelected 
                            ? "border-blue-500 bg-blue-500/10" 
                            : someSelected
                              ? "border-blue-500/50 bg-blue-500/5"
                              : "border-zinc-700 bg-zinc-800/30"
                        )}
                      >
                        {/* Account header */}
                        <div 
                          onClick={() => toggleAccountGroup(group.email)}
                          className="p-3 flex items-center justify-between cursor-pointer hover:bg-zinc-700/30"
                        >
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-[#C4A052]" />
                            <span className="text-white font-bold text-base">{group.email}</span>
                            <span className="text-zinc-400 text-sm">({group.totalQty} billets)</span>
                          </div>
                          <div>
                            {allSelected ? (
                              <span className="flex items-center gap-1 text-blue-400 font-bold">
                                <Check className="h-5 w-5" /> GARDER TOUT
                              </span>
                            ) : (
                              <span className="text-zinc-500">Cliquer pour sélectionner</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Tickets in this account */}
                        <div className="border-t border-zinc-700/50">
                          {group.tickets.map((achat, idx) => (
                            <div 
                              key={achat.id || idx}
                              onClick={() => toggleKeep(achat.id)}
                              className={cn(
                                "p-3 cursor-pointer transition-colors border-b border-zinc-700/30 last:border-b-0",
                                selectedToKeep.has(achat.id)
                                  ? "bg-blue-500/20"
                                  : "hover:bg-zinc-700/20"
                              )}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-white font-medium text-base">{achat.evenement}</p>
                                  <p className="text-white text-base mt-1">
                                    {achat.categorie} | {achat.email_compte || achat.mail_client} | Qty: {achat.qty}
                                    {achat.rang && achat.place && (
                                      <span className="text-[#C4A052]"> | Rang {achat.rang} Place {achat.place}</span>
                                    )}
                                  </p>
                                </div>
                                <div className="text-right">
                                  {selectedToKeep.has(achat.id) ? (
                                    <span className="text-blue-400 font-bold text-lg">✓ GARDER</span>
                                  ) : (
                                    <span className="text-zinc-500">Cliquer pour garder</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Selection info with validation */}
                <div className={cn(
                  "mt-3 p-3 rounded-lg border",
                  isSelectionValid 
                    ? "bg-emerald-500/10 border-emerald-500/30" 
                    : selectedQty > requiredQty
                      ? "bg-red-500/10 border-red-500/30"
                      : "bg-amber-500/10 border-amber-500/30"
                )}>
                  <p className="text-lg font-medium">
                    Sélectionné: <span className={cn(
                      "font-bold text-xl",
                      isSelectionValid ? "text-emerald-400" : selectedQty > requiredQty ? "text-red-400" : "text-amber-400"
                    )}>
                      {selectedQty}
                    </span> / {requiredQty} requis
                  </p>
                  {!isSelectionValid && (
                    <p className={cn(
                      "text-sm mt-1",
                      selectedQty > requiredQty ? "text-red-400" : "text-amber-400"
                    )}>
                      {selectedQty > requiredQty 
                        ? `⚠ Trop de billets sélectionnés (+${selectedQty - requiredQty}). Désélectionnez pour valider.`
                        : `⚠ Sélectionnez exactement ${requiredQty} billets pour valider.`
                      }
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleFixCurrent()}
                  disabled={fixing || !isSelectionValid}
                  className={cn(
                    "flex-1",
                    isSelectionValid 
                      ? "bg-emerald-600 hover:bg-emerald-500" 
                      : "bg-zinc-600 cursor-not-allowed"
                  )}
                >
                  {fixing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  {isSelectionValid ? `VALIDER (${selectedQty})` : `Sélection invalide (${selectedQty}/${requiredQty})`}
                </Button>
                <Button 
                  onClick={handleDesattribuerAll}
                  disabled={fixing}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-500"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Tout désattribuer
                </Button>
              </div>
            </>
          ) : activeTab === 'attributions' && doublons.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
              <p className="text-lg text-white">Aucune attribution en trop !</p>
              <p className="text-zinc-400 text-sm">Vérifiez l'onglet "VENTES dupliquées" si des doublons existent.</p>
            </div>
          ) : null}

          {/* Result message */}
          {result && (
            <div className={cn(
              "p-3 rounded flex items-center gap-2",
              result.ok ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
            )}>
              {result.ok ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {result.msg}
            </div>
          )}

          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full border-zinc-700">
            Fermer
          </Button>
        </div>

        {/* Alert Dialog for issues */}
        {alertDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-[#1a1a1a] border border-amber-500/50 rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="h-6 w-6 text-amber-500" />
                <h3 className="text-xl font-bold text-white">Attention</h3>
              </div>
              
              <div className="space-y-3 mb-6">
                {alertDialog.issues.map((issue, idx) => (
                  <div key={idx} className="p-3 bg-amber-500/10 rounded border border-amber-500/30">
                    <p className="text-amber-400">{issue.message}</p>
                  </div>
                ))}
              </div>
              
              {alertDialog.suggestion && (
                <div className="p-3 bg-blue-500/10 rounded border border-blue-500/30 mb-4">
                  <p className="text-blue-400 font-medium mb-2">💡 Suggestion:</p>
                  <p className="text-white">
                    Vous avez {alertDialog.suggestion.totalQty} billets disponibles chez{' '}
                    <span className="font-bold">{alertDialog.suggestion.email}</span>
                  </p>
                  <Button 
                    onClick={handleUseSuggestion}
                    size="sm"
                    className="mt-2 bg-blue-600 hover:bg-blue-500"
                  >
                    Utiliser cette suggestion
                  </Button>
                </div>
              )}
              
              <p className="text-white mb-4">Voulez-vous forcer l'attribution ?</p>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleAlertCancel}
                  variant="outline"
                  className="flex-1"
                >
                  NON
                </Button>
                <Button 
                  onClick={handleAlertForce}
                  className="flex-1 bg-amber-600 hover:bg-amber-500"
                >
                  OUI, Forcer
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DoublonsModal;
